// src/screens/PreviewScreen.js
//
// Explains the difference between the browser preview and the full
// SleepMixBlueprint Android app, with the release and contact links visitors
// need to learn more.

import React from 'react';
import {
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Radius, Fonts } from '../theme';

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.sleepmix.app';
const EMAIL = 'tyneil@sleepmixblueprint.com';
const WEBSITE_URL = 'https://tyneilallard.com';
const YOUTUBE_URL = 'https://www.youtube.com/@SleepMixBlueprint';

function LinkRow({ icon, title, subtitle, onPress, accessibilityLabel, last = false }) {
  return (
    <Pressable
      style={[styles.linkRow, last && styles.linkRowLast]}
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel || title}
    >
      <View style={styles.linkIconBubble}>
        <Ionicons name={icon} size={18} color={Colors.accent} />
      </View>
      <View style={styles.linkTextGroup}>
        <Text style={styles.linkTitle}>{title}</Text>
        <Text style={styles.linkSubtitle}>{subtitle}</Text>
      </View>
      <Ionicons name="chevron-forward" size={16} color={Colors.textMuted} />
    </Pressable>
  );
}

export default function PreviewScreen() {
  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.hero}>
        <View style={styles.heroIconRow}>
          <View style={styles.heroBadge}>
            <Ionicons name="phone-portrait-outline" size={18} color={Colors.accent} />
          </View>
          <Text style={styles.eyebrow}>PREVIEW</Text>
        </View>
        <Text style={styles.heroTitle}>Your SleepMixBlueprint preview and home base.</Text>
        <Text style={styles.heroLede}>
          SleepMixBlueprint is a full Android app on Google Play, but it is not on the Apple App
          Store just yet. sleepmixblueprint.com is only a preview — not the complete app
          experience. For everything else, tyneilallard.com is the home base for app releases,
          the cookbook, blog updates, and the YouTube channel.
        </Text>
      </View>

      <View style={styles.divider} />

      <View style={styles.appCard}>
        <View style={styles.appIconBubble}>
          <Ionicons name="download-outline" size={22} color={Colors.accent} />
        </View>
        <View style={styles.appCardBody}>
          <Text style={styles.appCardTitle}>Get the full Android app</Text>
          <Text style={styles.appCardText}>
            Find the complete SleepMixBlueprint experience, including the full sound library,
            on Google Play. The iOS version is not on the Apple App Store just yet.
          </Text>
          <Pressable
            style={styles.playButton}
            onPress={() => Linking.openURL(GOOGLE_PLAY_URL)}
            accessibilityRole="link"
            accessibilityLabel="Get SleepMixBlueprint on Google Play"
          >
            <Ionicons name="logo-google-playstore" size={17} color={Colors.textOnAccent} />
            <Text style={styles.playButtonText}>Get it on Google Play</Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.divider} />

      <Text style={styles.sectionLabel}>STAY IN TOUCH</Text>
      <View style={styles.linkList}>
        <LinkRow
          icon="mail-outline"
          title={EMAIL}
          subtitle="Questions about SleepMixBlueprint"
          onPress={() => Linking.openURL(`mailto:${EMAIL}`)}
        />
        <LinkRow
          icon="globe-outline"
          title="tyneilallard.com — the hub"
          subtitle="App releases, cookbook, blog, and more"
          onPress={() => Linking.openURL(WEBSITE_URL)}
        />
        <LinkRow
          icon="logo-youtube"
          title="@SleepMixBlueprint on YouTube"
          subtitle="Watch and subscribe to the channel"
          onPress={() => Linking.openURL(YOUTUBE_URL)}
          last
        />
      </View>

      <Text style={styles.footer}>
        Thanks for stopping by — we hope the preview helps you find your perfect mix.
      </Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  content: {
    padding: 20,
    paddingBottom: 48,
  },
  hero: {
    paddingTop: 8,
    paddingBottom: 24,
  },
  heroIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
  },
  heroBadge: {
    width: 34,
    height: 34,
    borderRadius: Radius.sm,
    backgroundColor: Colors.accent + '22',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eyebrow: {
    fontFamily: Fonts.bold,
    fontSize: 11,
    letterSpacing: 3,
    color: Colors.accent,
  },
  heroTitle: {
    fontFamily: Fonts.bold,
    fontSize: 22,
    color: Colors.textBright,
    lineHeight: 30,
    marginBottom: 10,
  },
  heroLede: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textSecondary,
    lineHeight: 22,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border,
    marginBottom: 20,
  },
  appCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    padding: 16,
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.accent + '55',
    marginBottom: 28,
  },
  appIconBubble: {
    width: 42,
    height: 42,
    borderRadius: Radius.md,
    backgroundColor: Colors.accent + '18',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  appCardBody: {
    flex: 1,
  },
  appCardTitle: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    color: Colors.textBright,
    marginBottom: 6,
  },
  appCardText: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    lineHeight: 20,
    color: Colors.textSecondary,
    marginBottom: 14,
  },
  playButton: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Colors.accent,
    borderRadius: Radius.pill,
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  playButtonText: {
    fontFamily: Fonts.semiBold,
    fontSize: 12,
    color: Colors.textOnAccent,
  },
  sectionLabel: {
    fontFamily: Fonts.bold,
    fontSize: 11,
    letterSpacing: 2,
    color: Colors.textMuted,
    marginBottom: 14,
  },
  linkList: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    overflow: 'hidden',
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  linkRowLast: {
    borderBottomWidth: 0,
  },
  linkIconBubble: {
    width: 36,
    height: 36,
    borderRadius: Radius.md,
    backgroundColor: Colors.accent + '18',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  linkTextGroup: {
    flex: 1,
  },
  linkTitle: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.textBright,
  },
  linkSubtitle: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textMuted,
    lineHeight: 17,
    marginTop: 1,
  },
  footer: {
    marginTop: 28,
    fontFamily: Fonts.regular,
    fontSize: 12,
    lineHeight: 18,
    color: Colors.textMuted,
    textAlign: 'center',
  },
});